import { useQsFilters } from "@dash/filters";
import { Page, PageHeader } from "@dash/page";
import { SearchWithPaginationSticky } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";

import { ItemSortField, itemsSortFields } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";
import { Serializers, SerializerValue, SortFilter } from "@megaron/serializers";

import { ItemSyncOptionsBar } from "./ItemSyncOptionsBar";
import { TradeItemsFilters } from "./TradeItemsFilters";
import { TradeItemsTable } from "./TradeItemsTable";

const filtersSerializer = Serializers.object({
  searchText: Serializers.string,
  sort: Serializers.sortFilter(itemsSortFields).asJsonString().optional(),
  isLoyalty: Serializers.jsonString(Serializers.boolean).optional(),
  groups: Serializers.jsonString(Serializers.array(Serializers.string)),
  isArchived: Serializers.jsonString(Serializers.boolean).optional(),
  isInAvailability: Serializers.jsonString(Serializers.boolean).optional(),
});

export type ItemsFilters = SerializerValue<typeof filtersSerializer>;

export type SortField = SortFilter<ItemSortField>;

export const TradeItemsPage = () => {
  const { filters, setFilter } = useQsFilters(filtersSerializer, {
    searchText: "",
    sort: undefined,
    isLoyalty: undefined,
    groups: [],
    isArchived: undefined,
    isInAvailability: undefined,
  });

  const itemsQuery = useClientManager("docs").searchItems().useQuery({
    searchText: filters.searchText,
    sort: filters.sort,
    groups: filters.groups,
    isArchived: filters.isArchived,
    isLoyalty: filters.isLoyalty,
    isInAvailability: filters.isInAvailability,
  });

  return (
    <Page
      css={{
        maxWidth: "none",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <PageHeader>Produkty</PageHeader>
      <ItemSyncOptionsBar />

      <SearchWithPaginationSticky
        onSearchChange={setFilter("searchText")}
        searchValue={filters.searchText}
        hidePagination
      />

      <TradeItemsFilters
        filters={filters}
        handlers={{
          onGroupsChange: setFilter("groups"),
          onIsArchivedChange: setFilter("isArchived"),
          onIsLoyaltyChange: setFilter("isLoyalty"),
          onSortChange: setFilter("sort"),
          onIsInAvailabilityChange: setFilter("isInAvailability"),
        }}
      />

      <QuerySkeleton query={itemsQuery}>
        {(items) => (
          <TradeItemsTable
            items={items.items}
            isLoading={itemsQuery.isFetching}
            activeSortValue={filters.sort}
            onSortChange={setFilter("sort")}
          />
        )}
      </QuerySkeleton>
    </Page>
  );
};
